import React, { useMemo } from 'react';
import { VenueProfileContext } from '../../context';
import { Venues } from '@weddingspot/ws-api-client';
import { getMonths } from '@weddingspot/ws-core-components';
import { Badge } from '../../../ui';

import styles from './ShortInfo.module.scss';

const ShortInfo = () => {
    const context = React.useContext(VenueProfileContext);

    const wsVenueProfile = React.useMemo(() => context.wsVenueProfile, []) as Venues.VenueDetails;
    const peakSeason = useMemo<string | null>(() => {
        if (!wsVenueProfile.peak_season) {
            return null;
        }
        const months = getMonths();
        let currentInterval = 0;
        const intervals = [];
        wsVenueProfile.peak_season.forEach((value, index) => {
            if (!intervals[currentInterval]) {
                intervals[currentInterval] = months[value - 1];
                if (wsVenueProfile.peak_season[index + 1] !== value + 1) {
                    currentInterval += 1;
                }
            } else if (wsVenueProfile.peak_season.length === index + 1 || value + 1 !== wsVenueProfile.peak_season[index + 1]) {
                intervals[currentInterval] += `-${months[value - 1]}`;
            }
        });
        return intervals.join(',');
    }, [wsVenueProfile.peak_season]);
    const isShortPrice = !!wsVenueProfile.cost_min && Math.floor(wsVenueProfile.cost_min / 1000) > 0;
    const getStyleSrpUrl = (longName: string) => {

        const removeIllegalCharacters = /[ /(.&]+/g; // Regex pattern that matches one or more occurrences of a slash, space, period, ampersand or opening parenthesis
        const removeClosingBracket = /\)/g; // Regex pattern that matches one or more occurrences of a closing parenthesis

        let styleName = longName.replace(removeIllegalCharacters, '-').replace(removeClosingBracket, '').toLowerCase();
        let region = wsVenueProfile.region.replace(removeIllegalCharacters, '-').replace(removeClosingBracket, '').toLowerCase();
        let market = wsVenueProfile.market.replace(removeIllegalCharacters, '-').toLowerCase();

        return `/wedding-venues/${styleName}-weddings/${market}/${region}`;
    }

    const addServiceTypes = (type: string, serviceTypes: Set<String>) => {
        if (type.indexOf('indoor') > -1) {
            serviceTypes.add('Indoor');
        }
        if (type.indexOf('outdoor') > -1) {
            serviceTypes.add('Outdoor');
        }
    }

    const addServiceCategories = (type: string, serviceCategories: Set<String>) => {
        if (type.indexOf('ceremony') > -1) {
            serviceCategories.add('Ceremony');
        }
        if (type.indexOf('reception') > -1) {
            serviceCategories.add('Reception');
        }
    }

    let serviceCategories = new Set<String>();
    let serviceTypes = new Set<String>();

    if (wsVenueProfile.services?.length > 0) {
        wsVenueProfile.services.forEach((item) => {
            const type = item.service_type;
            addServiceTypes(type, serviceTypes);
            addServiceCategories(type, serviceCategories);
        });
    }

    if (wsVenueProfile.services_rds?.length > 0) {
        serviceCategories.add('Rehearsal Dinner / Bridal Shower');

        wsVenueProfile.services_rds.forEach((rds_service) => {
            const type = rds_service.service_type;
            addServiceTypes(type, serviceTypes);
        })
    }

    const serviceTypesDisplayValue = Array.from(serviceTypes).sort().join(' / ');
    const serviceCategoriesDisplayValue = Array.from(serviceCategories).sort().join(' / ');

    return (
        <div className={styles.ShortInfo}>
            <div className={styles.info}>
                <div>
                    <div className={styles.capacity}>
                        <h2>Venue capacity</h2>
                        <p>up to {wsVenueProfile.max_capacity} guests</p>
                    </div>
                    { !!wsVenueProfile.cost_min && (
                        <div className={styles.price}>
                            <h2>Starting price</h2>
                            <p>${isShortPrice ? (Math.floor(wsVenueProfile.cost_min / 1000) + 'K ') : wsVenueProfile.cost_min}
                            to start</p>
                        </div>)
                    }
                    <div className={styles.season}>
                        {peakSeason ? (
                            <>
                                <h2>Peak Season</h2>
                                <p>{peakSeason}</p>
                            </>
                        ) : null}
                    </div>
                    { !wsVenueProfile.cost_min && (<div className={styles.price}></div>) }
                </div>

                <div>
                    {serviceTypesDisplayValue && serviceCategoriesDisplayValue ? (
                        <div className={styles.services}>
                            <h2>Services</h2>
                            <div>
                                <Badge key={serviceCategoriesDisplayValue}>{serviceCategoriesDisplayValue}</Badge>
                            </div>
                            <div>
                                <Badge key={serviceTypesDisplayValue}>{serviceTypesDisplayValue}</Badge>
                            </div>
                        </div>
                    ) : null}
                    {wsVenueProfile.styles?.length ? (
                        <div className={styles.styles}>
                            <h2>Styles</h2>
                            <div>
                                {wsVenueProfile.styles.map((item) => (
                                    <a href={`${getStyleSrpUrl(item.long_name)}`} target='_blank'>
                                    <Badge key={item.name}>{item.long_name}</Badge></a>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
            {wsVenueProfile.awards?.length ? (
                <div className={styles.awardsSection}>
                    <h2>Awards</h2>
                    <div className={styles.awards}>
                        {wsVenueProfile.awards.reverse().map((item) => (
                            <a href={item.page_url} target='_blank'>
                                <img src={item.image_url} alt={`Award ${item.name}`} />
                            </a>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ShortInfo;
